import { useEffect, useRef, useState } from 'react'
import { fetchPortfolioSummary } from '../../api/portfolio'
import { fetchMarketState } from '../../api/market'
import type { PortfolioSummary } from '../../types/portfolio'
import type { PortfolioDeltaMessage } from '../../types/delta'
import { useAuth } from '../../auth/AuthContext'
import { useShimmerOnUpdate } from '../../hooks/useShimmerOnUpdate'
import { Spinner } from '../common/Spinner'

function fmtUsd(v: string | number): string {
  const n = typeof v === 'string' ? parseFloat(v) : v
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
function hfColor(hf: number) {
  if (hf >= 2.0)  return 'text-emerald-400'
  if (hf >= 1.55) return 'text-amber-400'
  return 'text-red-400'
}
function hfBg(hf: number) {
  if (hf >= 2.0)  return 'bg-emerald-400/10 border-emerald-400/20'
  if (hf >= 1.55) return 'bg-yellow-400/10 border-yellow-400/20'
  return 'bg-red-400/10 border-red-400/20'
}
function apyColor(apy: number) {
  if (apy >= 10)  return 'text-emerald-400'
  if (apy >= 3)   return 'text-cyan-400'
  if (apy >= 0)   return 'text-ink-soft'
  return 'text-red-400'
}

// Aggregate USD values computed from the data panels; passed down from Dashboard
// so PortfolioOverview never makes duplicate wallet/lp/lending/perp API calls.
export interface PanelAgg {
  walletUsd: number
  lpUsd: number
  lendingUsd: number
  borrowUsd: number
  perpUsd: number
  claimableUsd: number
}

interface Props {
  portfolioId: string
  /** Partial aggregates — updated independently as each data panel reports in */
  agg?: Partial<PanelAgg>
  /** Latest delta from the parent's WebSocket connection — updates displayed summary */
  wsUpdate?: PortfolioDeltaMessage | null
  /** Called once when portfolio summary is successfully fetched for the first time */
  onPortfolioReady?: () => void
  /** Called every time a new summary is available (WS update or REST refresh) */
  onSummaryUpdate?: (s: PortfolioSummary) => void
}

export function PortfolioOverview({ portfolioId, agg, wsUpdate, onPortfolioReady, onSummaryUpdate }: Props) {
  const { token } = useAuth()
  const [summary, setSummary] = useState<PortfolioSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [copied, setCopied]   = useState(false)
  const [solPrice, setSolPrice] = useState<number | null>(null)
  const notifiedReady = useRef(false)
  const shimmerRef = useShimmerOnUpdate(wsUpdate)

  // Initial REST fetch — gives us data before the first WS push arrives
  useEffect(() => {
    if (!token) return
    if (!notifiedReady.current) setLoading(true)
    fetchPortfolioSummary(portfolioId)
      .then(s => {
        if (!s) return
        setSummary(s)
        onSummaryUpdate?.(s)
        if (!notifiedReady.current) { notifiedReady.current = true; onPortfolioReady?.() }
      })
      .catch(() => {})
      .finally(() => { setLoading(false) })
  }, [portfolioId, token])

  useEffect(() => {
    fetchMarketState('SOL')
      .then(s => { if (s?.currentPrice > 0) setSolPrice(s.currentPrice) })
      .catch(() => {})
  }, [])

  // Live updates from parent's WS connection
  useEffect(() => {
    if (!wsUpdate) return
    setSummary(wsUpdate)
    onSummaryUpdate?.(wsUpdate)
    if (!notifiedReady.current) { notifiedReady.current = true; onPortfolioReady?.(); setLoading(false) }
  }, [wsUpdate])

  const copy = () => {
    navigator.clipboard.writeText(portfolioId)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  if (loading && !summary) return (
    <div className="bg-surface-card rounded-xl p-6 flex justify-center py-10 border border-fringe">
      <Spinner size="lg" />
    </div>
  )
  if (!summary) return null

  const hf = parseFloat(summary.healthFactor)
  const hasDebt = parseFloat(summary.totalDebtUsd) > 0

  return (
    <div ref={shimmerRef} className="bg-surface-card rounded-xl border border-fringe border-spectral font-sans overflow-hidden">
      {/* Top bar: identity + timestamp */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-fringe">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-accent-purple/15 flex items-center justify-center shrink-0">
            <svg viewBox="0 0 24 24" fill="none" stroke="#6D5BF7" strokeWidth="1.5" strokeLinecap="round" className="w-4 h-4">
              <rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/>
            </svg>
          </div>
          <div>
            <p className="text-xs font-semibold text-ink tracking-wide">Portfolio Summary</p>
            <button
              onClick={copy}
              className="flex items-center gap-1 group mt-0.5"
              title="Copy address"
            >
              <span className="font-mono text-[11px] text-ink-muted group-hover:text-ink transition-colors truncate max-w-[140px] sm:max-w-none">
                {portfolioId}
              </span>
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"
                className={`w-3 h-3 shrink-0 transition-colors ${copied ? 'text-emerald-400' : 'text-gray-400 group-hover:text-ink-muted'}`}>
                {copied
                  ? <polyline points="2,8 6,12 14,4" />
                  : <><rect x="5" y="5" width="8" height="8" rx="1"/><path d="M11 5V3H3v8h2"/></>
                }
              </svg>
            </button>
          </div>
        </div>
        <p className="text-[11px] text-gray-400 shrink-0">
          {new Date(summary.computedAt).toLocaleTimeString()}
        </p>
      </div>

      <div className="px-3 sm:px-5 py-4">
        {/* Total Portfolio Value */}
        <div className="mb-4 sm:mb-5">
          <p className="text-xs text-gray-500 uppercase tracking-widest mb-1">Total Portfolio Value</p>
          <p className="text-2xl sm:text-4xl font-bold text-ink tracking-tight">{fmtUsd(summary.navUsd)}</p>
          {solPrice != null && solPrice > 0 && (
            <p className="text-xs text-gray-500 mt-1">
              ≈ {(parseFloat(summary.navUsd) / solPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} SOL
            </p>
          )}
        </div>

        {/* Metric grid */}
        <div className="grid grid-cols-2 gap-3">

          {/* Wallet */}
          <MetricCard
            label="Wallet"
            value={agg?.walletUsd != null ? fmtUsd(agg.walletUsd) : '—'}
            valueClass="text-cyan-400"
            labelClass="text-cyan-400/70"
            icon={<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="w-3.5 h-3.5 shrink-0"><path d="M17 10V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-2"/><path d="M13 10a2 2 0 0 0 0 4h4v-4h-4z"/></svg>}
          />

          {/* LP */}
          <MetricCard
            label="LP Size"
            value={agg?.lpUsd != null ? fmtUsd(agg.lpUsd) : '—'}
            valueClass="text-emerald-400"
            labelClass="text-emerald-400/70"
            icon={<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="w-3.5 h-3.5 shrink-0"><path d="M2 10c2-3 4-5 5-5s3 3 5 3 3-3 5-3"/></svg>}
          />

          {/* Lending */}
          <MetricCard
            label="Lending"
            value={agg?.lendingUsd != null ? fmtUsd(agg.lendingUsd) : '—'}
            valueClass="text-violet-400"
            labelClass="text-violet-400/70"
            icon={<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="w-3.5 h-3.5 shrink-0"><rect x="2" y="8" width="2.5" height="9" rx="0.5"/><rect x="8.75" y="5" width="2.5" height="12" rx="0.5"/><rect x="15.5" y="2" width="2.5" height="15" rx="0.5"/></svg>}
          />

          {/* Borrow */}
          <MetricCard
            label="Borrow"
            value={agg?.borrowUsd != null ? fmtUsd(agg.borrowUsd) : '—'}
            valueClass={agg?.borrowUsd != null && agg.borrowUsd > 0 ? 'text-red-400' : 'text-gray-500'}
            labelClass="text-red-400/70"
            icon={<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="w-3.5 h-3.5 shrink-0"><path d="M10 3v14M3 10h14"/></svg>}
          />

          {/* Claimable LP yield */}
          <MetricCard
            label="Claimable"
            value={agg?.claimableUsd != null ? fmtUsd(agg.claimableUsd) : '—'}
            valueClass="text-emerald-400"
            labelClass="text-emerald-400/70"
            icon={<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="w-3.5 h-3.5 shrink-0"><path d="M10 3v10M6 9l4 4 4-4"/><path d="M4 17h12"/></svg>}
            hint="LP fees + rewards"
          />

          {/* Perpetuals */}
          <MetricCard
            label="Perpetuals"
            value={agg?.perpUsd != null ? fmtUsd(agg.perpUsd) : '—'}
            valueClass="text-amber-400"
            labelClass="text-amber-400/70"
            icon={<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="w-3.5 h-3.5 shrink-0"><polyline points="2,14 6,9 11,13 18,6"/></svg>}
          />

          {/* Health Factor */}
          {hasDebt && (
            <div className={`rounded-lg px-3 py-2 border flex items-center justify-between gap-2 ${hfBg(hf)}`}>
              <div className="flex items-center gap-1.5">
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className={`w-3.5 h-3.5 shrink-0 ${hfColor(hf)}`}>
                  <path d="M10 3l1.6 4.9H17l-4.4 3.2 1.7 4.9L10 13l-4.3 3 1.7-4.9L3 8.9h5.4z"/>
                </svg>
                <div>
                  <p className={`text-[10px] font-medium uppercase tracking-wider ${hfColor(hf)}`}>Health Factor</p>
                  <p className="text-[9px] text-gray-500">{hf >= 2.0 ? 'Safe' : hf >= 1.55 ? 'Caution' : 'At Risk'}</p>
                </div>
              </div>
              <p className={`text-sm font-bold tracking-tight shrink-0 ${hfColor(hf)}`}>
                {hf >= 999990 ? '∞' : hf.toFixed(2)}
              </p>
            </div>
          )}

          {/* Net APY */}
          <div className="bg-surface-hover rounded-lg px-3 py-2 border border-fringe flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5">
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="w-3.5 h-3.5 shrink-0 text-ink-muted">
                <path d="M3 17l4-8 4 4 4-10"/>
              </svg>
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wider text-ink-muted">Net APY</p>
                <p className="text-[9px] text-gray-400">annualised</p>
              </div>
            </div>
            <p className={`text-sm font-bold tracking-tight shrink-0 ${summary.netApyPct != null ? apyColor(parseFloat(summary.netApyPct)) : 'text-gray-500'}`}>
              {summary.netApyPct != null ? parseFloat(summary.netApyPct).toFixed(1) + '%' : '—'}
            </p>
          </div>

        </div>
      </div>
    </div>
  )
}

function MetricCard({
  label, value, valueClass, icon, labelClass, hint,
}: {
  label: string; value: string; valueClass?: string
  icon: React.ReactNode; labelClass?: string; hint?: string
}) {
  return (
    <div className="bg-surface-hover rounded-lg px-3 py-2 border border-fringe flex items-center justify-between gap-2">
      <div className="flex items-center gap-1.5 min-w-0">
        <span className={`shrink-0 ${labelClass ?? 'text-ink-muted'}`}>{icon}</span>
        <div className="min-w-0">
          <p className={`text-[10px] font-medium uppercase tracking-wider truncate ${labelClass ?? 'text-ink-muted'}`}>{label}</p>
          {hint && <p className="text-[9px] text-gray-400">{hint}</p>}
        </div>
      </div>
      <p className={`text-sm font-bold tracking-tight shrink-0 ${valueClass ?? 'text-ink'}`}>{value}</p>
    </div>
  )
}
